import { ReactElement, ReactNode, useEffect, useId, useRef, useState } from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import Grow from '@mui/material/Grow';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Stack from '@mui/material/Stack';
import { styled, SxProps, Theme, useThemeProps } from '@mui/material/styles';
import Typography from '@mui/material/Typography';

import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

import { useEditMode } from '../../contextProviders/EditModeProvider';

import { GratiAdminOnlyProps, GratiAdminOnlyStrip, GratiAdminOnlyTag } from './GratiAdminOnly';
import GratiPublishSwitch from './GratiPublishSwitch';

// 'filled' = a section (top level in a page or tab); 'outlined' = a subsection nested inside one.
export type GratiSectionHeaderStyle = 'none' | 'filled' | 'outlined';

export interface GratiSectionAction {
  icon?: ReactElement;
  text?: string;
  /** What an icon-only action does, for screen readers and the hover tooltip, e.g. "Edit settings".
   *  Every icon-only action needs one; ignored when `text` is shown. */
  label?: string;
  onClick: () => void;
  /** Button variant for the primary action only (ignored for secondary/overflow actions and the
   *  icon-only case). Default renders as a plain text button, matching every existing caller. */
  variant?: 'text' | 'outlined' | 'contained';
  /** Only admins have this action — the ⋮ menu lists it under "Admins only", after the actions
   *  members have too. (The caller still decides who gets it; this only says which group.) */
  isAdminOnly?: boolean;
}

/** Content that stays a draft, visible to admins only, until it's published — see GratiSection's
 *  `publish`. Pass it only to admins. */
/** Marks every action as admin-only — for a ⋮ menu only admins get at all. */
export function asAdminOnly(actions: GratiSectionAction[] | undefined): GratiSectionAction[] | undefined {
  return actions?.map((action) => ({ ...action, isAdminOnly: true }));
}

export interface GratiSectionPublish {
  isPublished: boolean;
  onPublish: () => void;
  /** The way back to a draft. Without it the switch locks once published. */
  onUnpublish?: () => void;
  /** Publishing isn't possible yet (e.g. no scores entered): the switch is disabled while a draft. */
  isPublishDisabled?: boolean;
  /** Why publishing isn't possible yet, e.g. "No scores yet" — shown under the title while the
   *  switch is disabled, and given to the switch as its description. */
  disabledReason?: string;
  /** The inline question before unpublishing. Default "Hide this from members?". */
  unpublishPrompt?: string;
}

export interface GratiSectionProps {
  title?: string;
  /** Icon or element rendered inline immediately after the title text. */
  titleAdornment?: ReactNode;
  subtitle?: string;
  /** Visual style of the section header. Default: 'filled' */
  headerStyle?: GratiSectionHeaderStyle;
  /** Arbitrary content rendered in the header row before the primary action (e.g. ToggleButtonGroup) */
  headerChildren?: ReactNode;
  /** Primary action shown as a button/icon in the header row */
  primaryAction?: GratiSectionAction;
  /** Secondary actions shown as a MoreVert overflow menu */
  secondaryActions?: GratiSectionAction[];
  /** When true, highlights the section border + background to indicate edit mode — except under a
   *  tab-wide EditModeProvider, where the whole tab is editing and the Done FAB already says so */
  isEditing?: boolean;
  /** Color for the top accent bar. Defaults to divider color. Pass entity color (e.g. team.color) for identity. */
  color?: string;
  collapsible?: boolean;
  defaultExpanded?: boolean;
  /** Entering edit mode opens a collapsed section, since editing is done on its content. Off for
   *  a section collapsed because it's out of date (a past tee sheet round), which stays as it was. */
  isExpandedOnEdit?: boolean;
  /** Only admins can see this section: "· admins only" after the title, or, when the open section
   *  offers an action that changes that (e.g. Publish), a strip at the top of the content with the
   *  action on the right. See GratiAdminOnly. */
  adminOnly?: GratiAdminOnlyProps;
  /** Draft / Published, the same in every section: a switch in the header that says the state in
   *  words, with the header tinted while a draft (the theme's GratiSection header slot). An
   *  explicit `adminOnly` or `color` wins. */
  publish?: GratiSectionPublish;
  children?: ReactNode;
  className?: string;
  sx?: SxProps<Theme>;
}

interface GratiSectionOwnerState extends GratiSectionProps {
  headerStyle: GratiSectionHeaderStyle;
  expanded: boolean;
  color?: string;
  /** Bordered + tinted as the one section being edited (see `isEditing`). */
  isEditHighlighted: boolean;
  /** Set for a publishable section; the theme tints a draft's header. */
  publishState?: 'draft' | 'published';
}

const GratiSectionRoot = styled(Box, {
  name: 'GratiSection',
  slot: 'root',
  overridesResolver: (_props, styles) => styles.root,
})<{ ownerState: GratiSectionOwnerState }>(({ theme, ownerState }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  // Without this, a flex column item defaults to a min-width equal to its widest child's intrinsic
  // content size — a wide scrollable child (e.g. a many-column table) then forces the WHOLE section,
  // header included, to grow past its assigned 100% and blow out the page width, rather than being
  // clipped/scrolled within its own bounds the way the child's own overflow-x:auto already intends.
  // The standard flexbox fix, applied once here so no call site has to reinvent it per-section.
  minWidth: 0,
  ...(ownerState.isEditHighlighted && {
    borderRadius: theme.shape.borderRadius,
    border: `2px solid var(--mui-palette-primary-main)`,
    backgroundColor: `color-mix(in srgb, var(--mui-palette-primary-container) 15%, transparent)`,
    padding: theme.spacing(0.5),
  }),
}));

const GratiSectionColorbar = styled('div', {
  name: 'GratiSection',
  slot: 'colorbar',
  overridesResolver: (_props, styles) => styles.colorbar,
})<{ ownerState: GratiSectionOwnerState }>(() => ({
  height: '0.375rem',
  alignSelf: 'stretch',
}));

// The open section's actions, at the top of its content: the main action on the left; content
// controls (sort, toggles) and the ⋮ menu on the right. Opens and closes with the content, so a
// closed section shows no actions.
const GratiSectionToolbar = styled('div', {
  name: 'GratiSection',
  slot: 'toolbar',
  overridesResolver: (_props, styles) => styles.toolbar,
})(() => ({}));

const GratiSectionMenuAdminLabel = styled('li', {
  name: 'GratiSection',
  slot: 'menuAdminLabel',
  overridesResolver: (_props, styles) => styles.menuAdminLabel,
})(() => ({}));

const GratiSectionUnpublishConfirm = styled('div', {
  name: 'GratiSection',
  slot: 'unpublishConfirm',
  overridesResolver: (_props, styles) => styles.unpublishConfirm,
})(() => ({}));

const GratiSectionHeader = styled(Box, {
  name: 'GratiSection',
  slot: 'header',
  overridesResolver: (_props, styles) => styles.header,
})<{ ownerState: GratiSectionOwnerState }>(({ theme, ownerState }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(1),
  minHeight: '2.5rem',
  ...(ownerState.headerStyle === 'filled' && {
    backgroundColor: `var(--mui-palette-surface-containerHigh, ${theme.palette.action.hover})`,
    borderRadius: `${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0 0`,
    padding: theme.spacing(0.5, 1),
  }),
  // Subsection: the header row is outlined; the content below it is not boxed in
  ...(ownerState.headerStyle === 'outlined' && {
    border: `1px solid var(--mui-palette-divider, rgba(255,255,255,0.12))`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(0.5, 1),
  }),
}));

export default function GratiSection(inProps: GratiSectionProps) {
  const props = useThemeProps({ props: inProps, name: 'GratiSection' });

  const {
    title,
    titleAdornment,
    subtitle,
    headerStyle = 'filled',
    headerChildren,
    primaryAction,
    secondaryActions: ownSecondaryActions,
    isEditing = false,
    color: ownColor,
    collapsible = false,
    defaultExpanded = true,
    isExpandedOnEdit = true,
    adminOnly: ownAdminOnly,
    publish,
    children,
    className,
    sx,
  } = props;

  const [expanded, setExpanded] = useState(defaultExpanded);
  // Entering edit mode opens a collapsed section — editing is done on the content, so it has to show
  useEffect(() => {
    if (isEditing && collapsible && isExpandedOnEdit) setExpanded(true);
  }, [isEditing, collapsible, isExpandedOnEdit]);
  const [menuOpen, setMenuOpen] = useState(false);
  // canEdit is only ever true under an EditModeProvider (the default context is false)
  const { canEdit: isInTabEditMode } = useEditMode();
  const anchorRef = useRef<HTMLButtonElement>(null);

  const ownerState: GratiSectionOwnerState = {
    ...props,
    headerStyle,
    isEditing,
    isEditHighlighted: isEditing && !isInTabEditMode,
    expanded,
    publishState: publish ? (publish.isPublished ? 'published' : 'draft') : undefined,
  };

  const [isConfirmingUnpublish, setIsConfirmingUnpublish] = useState(false);
  // While publishing isn't possible yet, the reason joins the subtitle
  const publishReason = publish && !publish.isPublished && publish.isPublishDisabled ? publish.disabledReason : undefined;
  const shownSubtitle = [subtitle, publishReason].filter(Boolean).join(' · ') || undefined;
  const subtitleId = useId();
  const adminOnly = ownAdminOnly;
  const color = ownColor;
  const isAdminOnlyStrip = !!adminOnly?.action && (!collapsible || expanded);
  const secondaryActions = ownSecondaryActions;

  // While editing, the edit's own controls (Done, Cancel/Save) stay in the header beside the title
  const isActionInHeader = isEditing;
  const hasHeader = !!(title || shownSubtitle || publish || collapsible || (isActionInHeader && (primaryAction || headerChildren)));
  const hasToolbar = !!((!isActionInHeader && (primaryAction || headerChildren)) || (secondaryActions && secondaryActions.length > 0));
  const contentClassName = hasHeader ? 'GratiSection-contentBelowHeader' : undefined;

  const memberMenuActions = (secondaryActions ?? []).filter((action) => !action.isAdminOnly);
  const adminMenuActions = (secondaryActions ?? []).filter((action) => action.isAdminOnly);
  const renderMenuItem = (action: GratiSectionAction, key: string) => (
    <MenuItem key={key} onClick={(e) => handleMenuItemClick(e, action)}>
      {action.icon && (
        <Box component="span" sx={{ mr: 1, display: 'inline-flex', alignItems: 'center' }}>
          {action.icon}
        </Box>
      )}
      {action.text}
    </MenuItem>
  );

  const handleToggle = () => setExpanded((prev) => !prev);

  const handleToggleMenu = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setMenuOpen((prev) => !prev);
  };

  const handleCloseMenu = () => setMenuOpen(false);

  const handleMenuItemClick = (e: React.MouseEvent, action: GratiSectionAction) => {
    e.stopPropagation();
    handleCloseMenu();
    action.onClick();
  };

  const handleListKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Tab' || e.key === 'Escape') {
      e.preventDefault();
      setMenuOpen(false);
    }
  };

  /* Primary action:
       icon + text → Button with startIcon
       icon only   → IconButton
       text only   → text Button */
  const primaryActionButton = primaryAction && (
    primaryAction.icon && primaryAction.text ? (
      <Button size="small" variant={primaryAction.variant} startIcon={primaryAction.icon} onClick={primaryAction.onClick}>
        {primaryAction.text}
      </Button>
    ) : primaryAction.icon ? (
      <IconButton
        size="small"
        onClick={primaryAction.onClick}
        aria-label={primaryAction.label}
        title={primaryAction.label}
      >
        {primaryAction.icon}
      </IconButton>
    ) : (
      <Button size="small" variant={primaryAction.variant} onClick={primaryAction.onClick}>
        {primaryAction.text}
      </Button>
    )
  );

  // The open section's toolbar, at the top of its content
  const toolbar = hasToolbar && (
    <GratiSectionToolbar>
        {!isActionInHeader && primaryActionButton}
        <Box sx={{ flexGrow: 1 }} />
        {/* Content controls (sort, toggles) */}
        {!isActionInHeader && headerChildren}
        {/* Everything else — the ⋮ menu: members' actions first, then admins' */}
        {secondaryActions && secondaryActions.length > 0 && (
          <>
            <IconButton
              ref={anchorRef}
              size="small"
              onClick={handleToggleMenu}
              aria-label="More actions"
              aria-haspopup="true"
              aria-expanded={menuOpen ? 'true' : undefined}
            >
              <MoreVertIcon />
            </IconButton>
            <Popper
              open={menuOpen}
              anchorEl={anchorRef.current}
              role={undefined}
              placement="bottom-end"
              transition
              style={{ zIndex: 1300 }}
            >
              {({ TransitionProps }) => (
                <Grow {...TransitionProps} style={{ transformOrigin: 'right top' }}>
                  <Paper elevation={4}>
                    <ClickAwayListener onClickAway={handleCloseMenu}>
                      <MenuList autoFocusItem={menuOpen} onKeyDown={handleListKeyDown}>
                        {memberMenuActions.map((action, index) => renderMenuItem(action, `m${index}`))}
                        {adminMenuActions.length > 0 && memberMenuActions.length > 0 && <Divider />}
                        {adminMenuActions.length > 0 && (
                          <GratiSectionMenuAdminLabel>
                            <VisibilityOffIcon fontSize="inherit" />
                            Admins only
                          </GratiSectionMenuAdminLabel>
                        )}
                        {adminMenuActions.map((action, index) => renderMenuItem(action, `a${index}`))}
                      </MenuList>
                    </ClickAwayListener>
                  </Paper>
                </Grow>
              )}
            </Popper>
          </>
        )}
    </GratiSectionToolbar>
  );

  return (
    <GratiSectionRoot ownerState={ownerState} className={className} sx={sx}>
      {color && (
        <GratiSectionColorbar
          ownerState={ownerState}
          sx={{ backgroundColor: color }}
        />
      )}
      {hasHeader && (
        <GratiSectionHeader ownerState={ownerState}>
          {/* Left: title + subtitle */}
          {(title || shownSubtitle) && (
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              {title && (
                <Box
                  component="div"
                  onClick={collapsible ? handleToggle : undefined}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    minWidth: 0,
                    ...(collapsible && { cursor: 'pointer' }),
                  }}
                >
                  <Typography variant="titleMedium" color="text.primary" noWrap>
                    {title}
                  </Typography>
                  {titleAdornment}
                  {adminOnly && !isAdminOnlyStrip && <GratiAdminOnlyTag status={adminOnly.status} />}
                </Box>
              )}
              {shownSubtitle && (
                <Typography id={subtitleId} variant="bodySmall" noWrap component="div" sx={{
                  color: "text.secondary"
                }}>
                  {shownSubtitle}
                </Typography>
              )}
            </Box>
          )}
          {!title && !shownSubtitle && <Box sx={{ flexGrow: 1 }} />}

          {/* Publish state, in words, with the switch that changes it. Off asks first, below. */}
          {publish && (
            <GratiPublishSwitch
              isPublished={publish.isPublished}
              onPublish={publish.onPublish}
              onUnpublish={() => setIsConfirmingUnpublish(true)}
              disabled={publish.isPublished ? !publish.onUnpublish : publish.isPublishDisabled}
              describedBy={publishReason ? subtitleId : undefined}
            />
          )}

          {/* While editing: the edit's own controls (Done, Cancel/Save) */}
          {isActionInHeader && headerChildren}
          {isActionInHeader && primaryActionButton}

          {/* Collapse toggle */}
          {collapsible && (
            <IconButton size="small" onClick={handleToggle} aria-label={expanded ? 'Collapse section' : 'Expand section'}>
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          )}
        </GratiSectionHeader>
      )}
      {publish?.isPublished && publish.onUnpublish && isConfirmingUnpublish && (
        <GratiSectionUnpublishConfirm role="group" aria-label="Confirm unpublish">
          <Typography variant="bodyMedium">{publish.unpublishPrompt ?? 'Hide this from members?'}</Typography>
          <div>
            <Button size="small" autoFocus onClick={() => setIsConfirmingUnpublish(false)}>Cancel</Button>
            <Button
              size="small"
              variant="contained"
              color="warning"
              onClick={() => { setIsConfirmingUnpublish(false); publish.onUnpublish?.(); }}
            >
              Unpublish
            </Button>
          </div>
        </GratiSectionUnpublishConfirm>
      )}
      {/* Content — the class lets content that needs a gap below the header (e.g. GratiKey) ask for it */}
      {collapsible ? (
        <Collapse in={expanded} timeout={200}>
          <Stack spacing={1} className={contentClassName}>
            {isAdminOnlyStrip && <GratiAdminOnlyStrip {...adminOnly} />}
            {toolbar}
            {children}
          </Stack>
        </Collapse>
      ) : (
        <Stack spacing={0} className={contentClassName}>
          {isAdminOnlyStrip && <GratiAdminOnlyStrip {...adminOnly} />}
          {toolbar}
          {children}
        </Stack>
      )}
    </GratiSectionRoot>
  );
}
